import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import type { DrawerPayloadAction, WithOptional } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  stateHasFetchedAllObjectsSuccess,
} from 'utils/sliceHelpers';
import {
  EPaddingRuleType,
  type TPaddingRule,
  type TTravelTimePaddingDistance,
  type TTravelTimePaddingRule,
  createPaddingRule as create,
} from '@timeedit/types';
import api from '../services/api.service';

export interface TPaddingRulesState extends IDefaultAPIState<
  TPaddingRule,
  number
> {
  firstPaddingRule?: TPaddingRule;
  secondPaddingRule?: TPaddingRule;
}

export const initialState: TPaddingRulesState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  firstPaddingRule: undefined,
  secondPaddingRule: undefined,
};

// Slice
const slice = createSlice({
  name: 'paddingRules',
  initialState,
  reducers: {
    fetchPaddingRulesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchPaddingRulesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TCreatePaddingRulePayload>>
    ) => {
      stateHasFetchedAllObjectsSuccess<TCreatePaddingRulePayload, TPaddingRule>(
        payload,
        state,
        create,
        false
      );
      finishedLoadingSuccess(state);
    },
    fetchPaddingRulesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createPaddingRule: (
      state,
      { payload }: PayloadAction<EPaddingRuleType>
    ) => {
      if (state.firstPaddingRule?.id === 0) return;
      const newPaddingRule: TPaddingRule = {
        id: 0,
        name: '',
        paddingType: payload,
        includeAbstractObjects: false,
        description: '',
        rule:
          payload === EPaddingRuleType.DIRECT
            ? {
                objectsOnReservation: [],
                impactedObjects: [],
                paddingBefore: 0,
                paddingAfter: 0,
              }
            : {
                controllingTypeId: 0,
                controllingFieldId: 0,
                distances: [],
                impactedObjects: [],
              },
        history: [],
      };
      state.firstPaddingRule = newPaddingRule;
      state.results.unshift(newPaddingRule);
      state.map[0] = newPaddingRule;
      finishedLoadingSuccess(state);
    },
    savePaddingRuleRequest: (state) => {
      isLoadingRequest(state);
    },
    savePaddingRuleSuccess: (
      state,
      {
        payload: { isFirstPaddingRule },
      }: PayloadAction<{
        isFirstPaddingRule: boolean;
      }>
    ) => {
      if (isFirstPaddingRule) {
        state.firstPaddingRule = undefined;
      } else {
        state.secondPaddingRule = undefined;
      }
      finishedLoadingSuccess(state);
    },
    savePaddingRuleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deletePaddingRuleRequest: (state) => {
      isLoadingRequest(state);
    },
    deletePaddingRuleSuccess: (
      state,
      { payload: { paddingRuleId } }: PayloadAction<{ paddingRuleId: number }>
    ) => {
      deleteEntityFromState(paddingRuleId, state);
      if (state.firstPaddingRule?.id === paddingRuleId) {
        state.firstPaddingRule = undefined;
      } else {
        state.secondPaddingRule = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deletePaddingRuleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstPaddingRule: (
      state,
      { payload }: PayloadAction<TPaddingRule | undefined>
    ) => {
      const previousFirstPaddingRule = state.firstPaddingRule;
      state.firstPaddingRule = payload;
      if (!payload && previousFirstPaddingRule?.id === 0) {
        state.results = state.results.filter(
          (paddingRule: TPaddingRule) => paddingRule.id !== 0
        );
      }
    },
    setSecondPaddingRule: (
      state,
      { payload }: PayloadAction<TPaddingRule | undefined>
    ) => {
      state.secondPaddingRule = payload;
    },
    duplicatePaddingRule: (state, { payload }: PayloadAction<TPaddingRule>) => {
      if (state.firstPaddingRule?.id === 0) return;
      const newPaddingRule = {
        ...payload,
        id: 0,
        name: payload.name + '-copy',
        history: [],
      };
      state.firstPaddingRule = newPaddingRule;
      state.results.unshift(newPaddingRule);
      state.map[0] = newPaddingRule;
    },
    discardChanges: (
      state,
      { payload }: PayloadAction<{ id: number; isFirstDrawer: boolean }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstPaddingRule = state.map[id];
      } else {
        state.secondPaddingRule = state.map[id];
      }
    },
    changePaddingRule: <Key extends keyof TPaddingRule>(
      state: TPaddingRulesState,
      { payload }: DrawerPayloadAction<TPaddingRule, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstPaddingRule = {
          ...state.firstPaddingRule,
          [key]: value,
        };
      } else {
        state.secondPaddingRule = {
          ...state.secondPaddingRule,
          [key]: value,
        };
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const paddingRulesLoading = (state: TRootState) =>
  state.paddingRules.loading;
export const paddingRulesSelector = (state: TRootState) =>
  state.paddingRules.results;
export const paddingRuleMapSelector = (state: TRootState) =>
  state.paddingRules.map;
export const firstPaddingRuleSelector = (state: TRootState) =>
  state.paddingRules.firstPaddingRule;
export const secondPaddingRuleSelector = (state: TRootState) =>
  state.paddingRules.secondPaddingRule;

export const selectedIdsSelector = createSelector(
  [
    (state: TRootState) => state.paddingRules.firstPaddingRule,
    (state: TRootState) => state.paddingRules.secondPaddingRule,
  ],
  (firstPaddingRule, secondPaddingRule) =>
    _.compact([firstPaddingRule, secondPaddingRule]).map((pr) => pr.id)
);
// Actions
export const {
  fetchPaddingRulesRequest,
  fetchPaddingRulesSuccess,
  fetchPaddingRulesFailure,
  createPaddingRule,
  deletePaddingRuleRequest,
  deletePaddingRuleFailure,
  deletePaddingRuleSuccess,
  savePaddingRuleRequest,
  savePaddingRuleSuccess,
  savePaddingRuleFailure,
  setFirstPaddingRule,
  setSecondPaddingRule,
  duplicatePaddingRule,
  discardChanges,
  changePaddingRule,
} = slice.actions;

type TCreatePaddingRulePayload = WithOptional<
  TPaddingRule,
  'paddingType' | 'name' | 'description' | 'includeAbstractObjects' | 'rule'
>;

export const fetchPaddingRules = createAppAsyncThunk(
  'paddingRules/fetchPaddingRules',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchPaddingRulesRequest());
      const result = (await api.get({
        endpoint: `/padding-rules`,
      })) as IDefaultAPIResult<TCreatePaddingRulePayload>;
      dispatch(fetchPaddingRulesSuccess(result));
    } catch (e) {
      dispatch(fetchPaddingRulesFailure());
      console.error(e);
    }
  }
);

type TSavedPaddingRulePayload = {
  isFirstPaddingRule: boolean;
  paddingRule: TPaddingRule;
};

export const savePaddingRule = createAppAsyncThunk<
  void,
  TSavedPaddingRulePayload
>(
  'paddingRules/savePaddingRule',
  async ({ isFirstPaddingRule, paddingRule }, { dispatch }) => {
    try {
      dispatch(savePaddingRuleRequest());
      /**
       * Could be both either create or update
       * Depending on which, we need to use different methods (POST or PATCH)
       */
      if (paddingRule.id === 0) {
        // Create a new one
        const preppedPaddingRule = prepEntityForAPICall(
          paddingRule,
          true,
          true
        );
        delete preppedPaddingRule.history;
        await api.post({
          endpoint: `/padding-rules`,
          data: preppedPaddingRule,
        });
        dispatch(savePaddingRuleSuccess({ isFirstPaddingRule }));
        dispatch(fetchPaddingRules());
      } else {
        const preppedPaddingRule = prepEntityForAPICall(
          paddingRule,
          true,
          true
        );
        delete preppedPaddingRule.history;
        await api.patch({
          endpoint: `/padding-rules/${paddingRule.id}`,
          data: preppedPaddingRule,
        });
        dispatch(savePaddingRuleSuccess({ isFirstPaddingRule }));
        dispatch(fetchPaddingRules());
      }
    } catch (error) {
      dispatch(savePaddingRuleFailure());
      console.error(error);
    }
  }
);

export const deletePaddingRule = createAppAsyncThunk<void, number>(
  'paddingRules/deletePaddingRule',
  async (paddingRuleId, { dispatch }) => {
    try {
      dispatch(deletePaddingRuleRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (paddingRuleId !== 0)
        await api.delete({
          endpoint: `/padding-rules/${paddingRuleId}`,
        });
      dispatch(deletePaddingRuleSuccess({ paddingRuleId }));
    } catch (e) {
      dispatch(deletePaddingRuleFailure());
      console.error(e);
    }
  }
);

/**
 * Travel time padding rule has distances array, each of which has fieldValues array.
 * The order of array are changed after matrix component, so we need to sort them before comparing.
 **/
const checkPaddingRuleChanged = (
  paddingRule1: TPaddingRule,
  paddingRule2: TPaddingRule
): boolean => {
  if (paddingRule1.paddingType === EPaddingRuleType.DIRECT)
    return checkSavedDisabled(paddingRule1, paddingRule2);

  return checkSavedDisabled(
    normalizePaddingRule(paddingRule1),
    normalizePaddingRule(paddingRule2)
  );
};

function normalizePaddingRule(paddingRule: TPaddingRule): TPaddingRule {
  return {
    ...paddingRule,
    rule: {
      ...paddingRule.rule,
      distances: sortDistances(
        (paddingRule.rule as TTravelTimePaddingRule).distances || []
      ),
    },
  };
}

function sortDistances(
  distances: TTravelTimePaddingDistance[]
): TTravelTimePaddingDistance[] {
  const newDistances = distances
    .filter((distance) => distance.padding !== 0)
    .map((distance) => {
      return {
        ...distance,
        fieldValues: _.sortBy(
          distance.fieldValues,
          (fieldValue) => fieldValue
        ) as [string, string],
      };
    });
  newDistances.sort((a, b) => a.fieldValues[0].localeCompare(b.fieldValues[0]));
  return newDistances;
}

export const checkPaddingSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { paddingRules } = getState();
    const {
      firstPaddingRule,
      secondPaddingRule,
      map: paddingRuleMap,
    } = paddingRules;
    if (
      isFirstDrawer &&
      firstPaddingRule &&
      paddingRuleMap[firstPaddingRule.id]
    ) {
      return checkPaddingRuleChanged(
        firstPaddingRule,
        paddingRuleMap[firstPaddingRule.id]
      );
    }
    if (
      !isFirstDrawer &&
      secondPaddingRule &&
      paddingRuleMap[secondPaddingRule.id]
    ) {
      return checkPaddingRuleChanged(
        secondPaddingRule,
        paddingRuleMap[secondPaddingRule.id]
      );
    }

    return true;
  };

export const checkFirstPaddingRuleExist =
  (): TAppThunk<boolean> => (_, getState) =>
    !!getState().paddingRules.firstPaddingRule;
